import type { ComponentType } from "react";
import React from "react";
import { Route } from "react-router-dom";

import {
  LegacyProjectPageRedirect,
  LegacyProjectScopedContactsRedirect,
} from "../components/ProjectRouting/ProjectRouting";
import ProtectedRoute from "../components/ProtectedRoute";
import { LEGACY_CONTACTS_PAGE_PATH, PATHS, projectRoute } from "./paths";

// Each page is loaded in its own chunk (React.lazy) so the initial bundle only
// ships the code for the route actually being visited, instead of all 8 pages
// plus their dependencies (e.g. react-select). Reduces unused JS and TBT.
const MainPage = React.lazy(() => import("../pages/MainPage"));
const LoginPage = React.lazy(() => import("../pages/LoginPage"));
const TableContactsPage = React.lazy(
  () => import("../pages/TableContactsPage"),
);
const DncCheckPage = React.lazy(() => import("../pages/DncCheckPage"));
const TableCallsPage = React.lazy(() => import("../pages/TableCallsPage"));
const TableWebhookLogsPage = React.lazy(
  () => import("../pages/TableWebhookLogsPage"),
);
const SettingsPage = React.lazy(() => import("../pages/SettingsPage"));
const CalendarPage = React.lazy(
  () => import("../pages/CalendarPage/CalendarPage"),
);
const BotCallingCalendarPage = React.lazy(() =>
  import("../pages/CalendarPage/CalendarPage").then((module) => ({
    default: module.BotCallingCalendarPage,
  })),
);
const PropertyTimelinePage = React.lazy(
  () => import("../pages/PropertyTimelinePage/PropertyTimelinePage"),
);
const ProjectActivityPage = React.lazy(
  () => import("../pages/ProjectActivityPage/ProjectActivityPage"),
);
const LostLeadsPage = React.lazy(
  () => import("../pages/LostLeadsPage/LostLeadsPage"),
);
const AnalyticsPage = React.lazy(
  () => import("../pages/AnalyticsPage/AnalyticsPage"),
);
const VapiEmulatorAnswersPage = React.lazy(
  () => import("../pages/VapiEmulatorAnswersPage/VapiEmulatorAnswersPage"),
);
const VapiEmulatorLogsPage = React.lazy(
  () => import("../pages/VapiEmulatorLogsPage/VapiEmulatorLogsPage"),
);

const createRoute = (
  path: string,
  Component: ComponentType,
  isPublic = false,
) => (
  <Route
    key={path}
    path={path}
    element={
      isPublic ? (
        <Component />
      ) : (
        <ProtectedRoute>
          <Component />
        </ProtectedRoute>
      )
    }
  />
);

const createLegacyRedirect = (pagePath: string, targetPagePath?: string) => (
  <Route
    key={`legacy-${pagePath}`}
    path={pagePath}
    element={
      <ProtectedRoute>
        <LegacyProjectPageRedirect
          pagePath={pagePath}
          targetPagePath={targetPagePath}
        />
      </ProtectedRoute>
    }
  />
);

const createLegacyProjectScopedContactsRedirect = () => (
  <Route
    key="legacy-project-contacts"
    path="/:projectId/contacts"
    element={
      <ProtectedRoute>
        <LegacyProjectScopedContactsRedirect />
      </ProtectedRoute>
    }
  />
);

export const pageRoutes = [
  createRoute(PATHS.MAIN, MainPage, true),
  createRoute(PATHS.LOGIN, LoginPage, true),
  createRoute(projectRoute(PATHS.PROPERTY), TableContactsPage),
  createRoute(projectRoute(PATHS.DNC_CHECK), DncCheckPage),
  createRoute(projectRoute(PATHS.CALLS), TableCallsPage),
  createRoute(projectRoute(PATHS.WEBHOOK_LOGS), TableWebhookLogsPage),
  createRoute(projectRoute(PATHS.SETTINGS), SettingsPage),
  createRoute(
    projectRoute(PATHS.VAPI_EMULATOR_ANSWERS),
    VapiEmulatorAnswersPage,
  ),
  createRoute(projectRoute(PATHS.VAPI_EMULATOR_LOGS), VapiEmulatorLogsPage),
  createRoute(projectRoute(PATHS.CALENDAR), CalendarPage),
  createRoute(projectRoute(PATHS.CALLING_CALENDAR), BotCallingCalendarPage),
  createRoute(projectRoute(PATHS.LOST_LEADS), LostLeadsPage),
  createRoute(projectRoute(PATHS.ANALYTICS), AnalyticsPage),
  createRoute(projectRoute(PATHS.ACTIVITY), ProjectActivityPage),
  createRoute(
    "/:projectId/property/:propertyId/timeline",
    PropertyTimelinePage,
  ),
];

const legacyRedirects = [
  createLegacyRedirect(PATHS.PROPERTY),
  createLegacyRedirect(LEGACY_CONTACTS_PAGE_PATH, PATHS.PROPERTY),
  createLegacyProjectScopedContactsRedirect(),
  createLegacyRedirect(PATHS.DNC_CHECK),
  createLegacyRedirect(PATHS.CALLS),
  createLegacyRedirect(PATHS.WEBHOOK_LOGS),
  createLegacyRedirect(PATHS.SETTINGS),
  createLegacyRedirect(PATHS.VAPI_EMULATOR_ANSWERS),
  createLegacyRedirect(PATHS.VAPI_EMULATOR_LOGS),
  createLegacyRedirect(PATHS.CALENDAR),
];

export const routes = [...pageRoutes, ...legacyRedirects];
